import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";

export const loginUser = createAsyncThunk(
  "auth/login",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      console.log("🚀 Login attempt:", { email });
      console.log("🌐 API Base URL:", process.env.GATSBY_API_URL);

      // Client-side email validation
      if (!email.endsWith("@knovik.com")) {
        return rejectWithValue("Only @knovik.com email addresses are allowed");
      }

      const response = await api.post("/api/auth/login", { email, password });

      console.log("✅ Login successful:", response.data);

      // Extract tokens and user from backend response
      const { user, tokens } = response.data.data;
      localStorage.setItem("token", tokens.accessToken);

      return {
        user: user,
        token: tokens.accessToken,
      };
    } catch (error) {
      console.error("❌ Login failed:", error);
      console.error("📄 Error response:", error.response?.data);
      console.error("🔢 Error status:", error.response?.status);

      // Better error messages
      if (error.response?.status === 401) {
        return rejectWithValue(
          "Invalid email or password. Please check your credentials."
        );
      }
      if (error.response?.status === 400) {
        return rejectWithValue("Invalid login credentials. Please try again.");
      }
      if (!error.response) {
        return rejectWithValue(
          "Network error. Please check your connection and try again."
        );
      }

      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const registerUser = createAsyncThunk(
  "auth/register",
  async ({ email, password, name }, { rejectWithValue }) => {
    try {
      console.log("🚀 Registration attempt:", { email, name });
      console.log("🌐 API Base URL:", process.env.GATSBY_API_URL);

      // Client-side email validation
      if (!email.endsWith("@knovik.com")) {
        return rejectWithValue("Only @knovik.com email addresses are allowed");
      }

      const response = await api.post("/api/auth/register/company", {
        email,
        password,
        name,
      });

      console.log("✅ Registration successful:", response.data);

      // Extract tokens and user from backend response
      const { user, tokens } = response.data.data;
      localStorage.setItem("token", tokens.accessToken);

      return {
        user: user,
        token: tokens.accessToken,
      };
    } catch (error) {
      console.error("❌ Registration failed:", error);
      console.error("📄 Error response:", error.response?.data);
      console.error("🔢 Error status:", error.response?.status);

      // Better error messages
      if (error.response?.status === 409) {
        return rejectWithValue(
          "An account with this email already exists. Please try logging in instead."
        );
      }
      if (error.response?.status === 400) {
        const errorData = error.response?.data;
        if (errorData?.details && Array.isArray(errorData.details)) {
          const errorMessages = errorData.details
            .map((detail) => detail.message || detail)
            .join(", ");
          return rejectWithValue(`Registration failed: ${errorMessages}`);
        }
        return rejectWithValue(
          "Invalid registration data. Please check your information and try again."
        );
      }
      if (!error.response) {
        return rejectWithValue(
          "Network error. Please check your connection and try again."
        );
      }

      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const checkAuth = createAsyncThunk(
  "auth/checkAuth",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token");

      console.log(
        "🔍 Checking auth with token:",
        token ? "Token exists" : "No token"
      );

      const response = await api.get("/api/auth/me");

      console.log("✅ Auth check successful:", response.data);

      return {
        user: response.data.data?.user || response.data.user,
        token: token,
      };
    } catch (error) {
      console.error("❌ Auth check failed:", error);
      localStorage.removeItem("token");
      return rejectWithValue("Not authenticated");
    }
  }
);

export const initializeAuth = createAsyncThunk(
  "auth/initialize",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const token = localStorage.getItem("token");
      console.log("🚀 Initializing auth, token exists:", !!token);

      if (!token) {
        console.log("❌ No token found");
        return rejectWithValue("No token found");
      }

      // Use checkAuth to get real user data from the server
      console.log("🔍 Verifying token and fetching user data...");
      const result = await dispatch(checkAuth());

      if (checkAuth.fulfilled.match(result)) {
        console.log("✅ Auth initialization successful with real user data");
        return result.payload;
      } else {
        console.log("❌ Token verification failed");
        return rejectWithValue("Token verification failed");
      }
    } catch (error) {
      console.error("❌ Auth initialization failed:", error);
      localStorage.removeItem("token");
      return rejectWithValue("Authentication initialization failed");
    }
  }
);

// Check for existing token on initialization
const getInitialState = () => {
  if (typeof window !== "undefined") {
    const token = localStorage.getItem("token");
    return {
      user: null,
      token: token,
      isLoading: false,
      error: null,
      isAuthenticated: !!token,
      isInitialized: false,
    };
  }
  return {
    user: null,
    token: null,
    isLoading: false,
    error: null,
    isAuthenticated: false,
    isInitialized: false,
  };
};

const authSlice = createSlice({
  name: "auth",
  initialState: getInitialState(),
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.isInitialized = true;
      if (typeof window !== "undefined") {
        localStorage.removeItem("token");
      }
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.isInitialized = true;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.isInitialized = true;
      })
      .addCase(registerUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.isInitialized = true;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.isInitialized = true;
      })
      .addCase(checkAuth.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.isInitialized = true;
      })
      .addCase(checkAuth.rejected, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.isInitialized = true;
      })
      .addCase(initializeAuth.pending, (state) => {
        state.isInitialized = false;
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.isInitialized = true;
      })
      .addCase(initializeAuth.rejected, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.isInitialized = true;
      });
  },
});

export const { logout, clearError } = authSlice.actions;
export default authSlice.reducer;
